import React from 'react'
import { Button, Heading, Input, LoadingSpinner, Text } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import {
  comercialCalculadorasService,
} from '../../services/comercialCalculadoras'
import { userLogsService } from '../../services/userLogs'
import {
  CALCULADORA_PARAM_KEYS,
  type ComercialCalculadoraParameter,
} from '../../types/comercialCalculadoras'
import { fmtMoeda } from '../../utils/calculadoraNr1'

const PARAMETER_ORDER = [
  CALCULADORA_PARAM_KEYS.NR1_VALOR_INDIVIDUAL,
  CALCULADORA_PARAM_KEYS.NR1_COMISSAO_PERCENTUAL_MAXIMO,
]

const sortParameters = (items: ComercialCalculadoraParameter[]) =>
  [...items].sort(
    (a, b) =>
      PARAMETER_ORDER.indexOf(a.key as (typeof PARAMETER_ORDER)[number]) -
      PARAMETER_ORDER.indexOf(b.key as (typeof PARAMETER_ORDER)[number])
  )

const formatParameterValue = (param: ComercialCalculadoraParameter): string => {
  if (param.key === CALCULADORA_PARAM_KEYS.NR1_VALOR_INDIVIDUAL) {
    const value = parseFloat(param.value)
    return Number.isFinite(value) ? fmtMoeda(value) : param.value
  }
  if (param.key === CALCULADORA_PARAM_KEYS.NR1_COMISSAO_PERCENTUAL_MAXIMO) {
    return `${param.value}%`
  }
  return param.value
}

const CalculadoraParametrosTab: React.FC = () => {
  const [parameters, setParameters] = React.useState<ComercialCalculadoraParameter[]>([])
  const [loading, setLoading] = React.useState(true)
  const [editingParameter, setEditingParameter] = React.useState<ComercialCalculadoraParameter | null>(null)
  const [parameterEditValue, setParameterEditValue] = React.useState('')
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [saving, setSaving] = React.useState(false)

  const loadParameters = React.useCallback(async () => {
    setLoading(true)
    try {
      const data = await comercialCalculadorasService.listParameters()
      setParameters(sortParameters(data))
    } catch (error) {
      console.error('Erro ao carregar parâmetros das calculadoras:', error)
      setParameters([])
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    void loadParameters()
  }, [loadParameters])

  const openEditModal = (param: ComercialCalculadoraParameter) => {
    setEditingParameter(param)
    setParameterEditValue(param.value)
    setIsModalOpen(true)
  }

  const closeEditModal = () => {
    setIsModalOpen(false)
    setEditingParameter(null)
    setParameterEditValue('')
  }

  const isValidValue = (param: ComercialCalculadoraParameter, value: string) => {
    const trimmed = value.trim()
    if (!trimmed) return false
    const num = parseFloat(trimmed.replace(',', '.'))
    if (!Number.isFinite(num) || num < 0) return false
    if (param.key === CALCULADORA_PARAM_KEYS.NR1_COMISSAO_PERCENTUAL_MAXIMO && num > 100) return false
    return true
  }

  const saveParameter = async () => {
    if (!editingParameter || !isValidValue(editingParameter, parameterEditValue)) return

    setSaving(true)
    const oldValue = editingParameter.value
    const newValue = parameterEditValue.trim().replace(',', '.')

    try {
      await comercialCalculadorasService.updateParameter(editingParameter.id, { value: newValue })
      await userLogsService.logUpdate(
        'parameter',
        editingParameter.id,
        `Alterou parâmetro nas Calculadoras Comerciais: ${editingParameter.key}`,
        { value: oldValue },
        { value: newValue },
        { parameter_key: editingParameter.key, context: 'comercial_calculadoras' }
      )
      await loadParameters()
      closeEditModal()
    } catch (err: unknown) {
      console.error('Erro ao salvar parâmetro das calculadoras:', err)
      const message = err instanceof Error ? err.message : 'Erro ao salvar parâmetro. Tente novamente.'
      alert(message)
    } finally {
      setSaving(false)
    }
  }

  const inputPropsForParameter = (param: ComercialCalculadoraParameter | null) => {
    if (!param) return { type: 'text' as const, step: undefined, max: undefined }
    if (param.key === CALCULADORA_PARAM_KEYS.NR1_VALOR_INDIVIDUAL) {
      return { type: 'number' as const, step: '0.01', min: 0, max: undefined }
    }
    if (param.key === CALCULADORA_PARAM_KEYS.NR1_COMISSAO_PERCENTUAL_MAXIMO) {
      return { type: 'number' as const, step: '0.1', min: 0, max: 100 }
    }
    return { type: 'text' as const, step: undefined, max: undefined }
  }

  const inputProps = inputPropsForParameter(editingParameter)

  return (
    <>
      <div className="px-6 py-4">
        <div className="mb-6">
          <Text size="sm" color="muted">
            Configurações das calculadoras comerciais. Edite o valor conforme necessário.
          </Text>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-lg shadow">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/30">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Descrição
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Valor
                  </th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {parameters.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                      Nenhum parâmetro cadastrado.
                    </td>
                  </tr>
                ) : (
                  parameters.map((param) => (
                    <tr key={param.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td
                        className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 max-w-md"
                        title={param.description ?? undefined}
                      >
                        {param.description ?? '—'}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">
                        {formatParameterValue(param)}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <PermissionGuard
                          moduleName="comercial_calculadoras"
                          permissionType="update"
                          fallback={null}
                        >
                          <Button variant="outline" size="sm" onClick={() => openEditModal(param)}>
                            Editar
                          </Button>
                        </PermissionGuard>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isModalOpen && editingParameter && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={closeEditModal} />
            <div
              className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-4 pt-5 pb-4 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                    Editar parâmetro
                  </Heading>
                  <button
                    type="button"
                    onClick={closeEditModal}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                  >
                    <span className="sr-only">Fechar</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <Text size="sm" className="text-gray-600 dark:text-gray-400 mb-4 block font-mono">
                  {editingParameter.key}
                </Text>

                <div className="space-y-4">
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                      Valor *
                    </Text>
                    <Input
                      type={inputProps.type}
                      step={inputProps.step}
                      min={inputProps.min}
                      max={inputProps.max}
                      value={parameterEditValue}
                      onChange={(e) => setParameterEditValue(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1 block">
                      Descrição
                    </Text>
                    <Text size="sm" color="muted">
                      {editingParameter.description ?? '—'}
                    </Text>
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <Button variant="outline" onClick={closeEditModal}>
                    Cancelar
                  </Button>
                  <PermissionGuard
                    moduleName="comercial_calculadoras"
                    permissionType="update"
                    fallback={null}
                  >
                    <Button
                      variant="solid"
                      disabled={saving || !isValidValue(editingParameter, parameterEditValue)}
                      onClick={() => void saveParameter()}
                    >
                      {saving ? 'Salvando...' : 'Salvar'}
                    </Button>
                  </PermissionGuard>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default CalculadoraParametrosTab
